import { RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { merchantAuthAPI, MerchantAdvanceHistory } from '../lib/api';
import { timeUtils } from '../lib/utils';

export function MerchantTopupsHistoryPage() {
    const [history, setHistory] = useState<MerchantAdvanceHistory | null>(null);
    const [page, setPage] = useState(1);
    const [revision, setRevision] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(false);
        setHistory(null);
        merchantAuthAPI.getAdvances(page).then(result => {
            if (active) setHistory(result);
        }).catch(() => {
            if (active) setError(true);
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, [page, revision]);

    const refresh = () => setRevision(value => value + 1);
    return <section aria-label="История пополнений" aria-busy={loading} className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-500 dark:text-slate-400">Ручные переводы в USDT</p>
            <Button variant="outline" size="sm" disabled={loading} onClick={refresh}>
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Обновить
            </Button>
        </div>
        {loading && <p role="status">Загрузка пополнений…</p>}
        {error && <div className="space-y-3">
            <p role="alert" className="text-red-600 dark:text-red-300">Не удалось загрузить историю пополнений.</p>
            <Button variant="outline" onClick={refresh}>Повторить</Button>
        </div>}
        {history && (history.total === 0
            ? <p className="rounded-xl border border-slate-200 bg-white p-6 text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">Пополнений пока нет</p>
            : <>
                <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                    <table className="block w-full text-left text-sm sm:table sm:table-fixed">
                        <thead className="hidden bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-300 sm:table-header-group">
                            <tr><th scope="col" className="w-[30%] p-4">Дата</th><th scope="col" className="w-[25%] p-4">Сумма, USDT</th><th scope="col" className="p-4">Комментарий</th></tr>
                        </thead>
                        <tbody className="block divide-y divide-slate-200 dark:divide-slate-800 sm:table-row-group">
                            {history.items.map(item => <tr key={item.id} className="block sm:table-row">
                                <td className="block px-4 pt-4 align-top sm:table-cell sm:p-4">
                                    <span className="mb-1 block text-xs text-slate-500 dark:text-slate-400 sm:hidden">Дата</span>
                                    <time dateTime={timeUtils.parseUTC(item.created_at).toISOString()}>{timeUtils.parseUTC(item.created_at).toLocaleString('ru-RU')}</time>
                                </td>
                                <td className="block px-4 pt-3 align-top tabular-nums sm:table-cell sm:p-4">
                                    <span className="mb-1 block text-xs text-slate-500 dark:text-slate-400 sm:hidden">Сумма, USDT</span>
                                    <span>{item.kind === 'return' ? 'Поступление от мерчанта: +' : 'Перевод мерчанту: −'}{item.amount.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </td>
                                <td className="block break-words p-4 pt-3 align-top sm:table-cell sm:p-4">
                                    <span className="mb-1 block text-xs text-slate-500 dark:text-slate-400 sm:hidden">Комментарий</span>
                                    <span className="whitespace-pre-wrap">{item.merchant_comment || '—'}</span>
                                </td>
                            </tr>)}
                            {history.items.length === 0 && <tr><td colSpan={3} className="p-4">На этой странице нет пополнений.</td></tr>}
                        </tbody>
                    </table>
                </div>
                <nav aria-label="Страницы пополнений" className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-slate-500 dark:text-slate-400">Всего: {history.total}</p>
                    <div className="flex items-center gap-3">
                        <Button variant="outline" disabled={page <= 1} onClick={() => setPage(value => value - 1)}>Назад</Button>
                        <span className="text-sm">{history.page} / {history.pages}</span>
                        <Button variant="outline" disabled={page >= history.pages} onClick={() => setPage(value => value + 1)}>Далее</Button>
                    </div>
                </nav>
            </>)}
    </section>;
}
